import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useCountries from '@afosto/utils-react/useCountries';
import useForm from '@afosto/utils-react/validation/useForm';
import FiltersFormSection from './FiltersFormSection';
import ConstraintsFormSection from './ConstraintsFormSection';
import DefaultFormSection from './DefaultFormSection';
import Form from '@afosto/app-components-react/Form';
import VatSettingsFormSection from './VatSettingsFormSection';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import isDefined from '@afosto/utils/isDefined';
import { vat as vatApi } from '@afosto/catalog/api/Vat';
import validationSchema from './validations';

const VAT_SETTING_STRUCTURE = {
  type: 'fixed',
  reference: null,
  countryCode: 'NL',
};

const formatFilters = filters =>
  filters.reduce((acc, filter) => {
    const { key, operator, values } = filter;
    const valueNotDefined = values === undefined || values === null;
    const emptyArrayValue = Array.isArray(values) && values?.length === 0;

    if (!key || !operator || valueNotDefined || emptyArrayValue) {
      return acc;
    }

    const integerFields = ['per'];
    const formattedValue = integerFields.includes(key) ? parseInt(values, 10) : values;

    return [
      ...acc,
      {
        key,
        operator,
        values: formattedValue,
      },
    ];
  }, []);

const formatConstraints = constraints =>
  constraints.reduce((acc, constraint) => {
    const { key, operator, values } = constraint;
    const valueNotDefined = !isDefined(values);
    const emptyArrayValue = Array.isArray(values) && values?.length === 0;

    if (!key || !operator || valueNotDefined || emptyArrayValue) {
      return acc;
    }

    const integerFields = ['quantity', 'amount', 'filtered_amount'];
    const formattedValue = integerFields.includes(key) ? [parseInt(values, 10)] : values;

    return [
      ...acc,
      {
        key,
        operator,
        values: formattedValue,
      },
    ];
  }, []);

const ServiceCostForm = ({ service, onSubmit, onCancel }) => {
  const countries = useCountries();
  const [vatRates, setVatRates] = useState([]);

  const formattedVatSettings = (service?.vat_settings ?? []).reduce(
    (acc, settings) => [
      ...acc,
      {
        type: settings?.type,
        reference: findObjectInArray(vatRates, 'value', settings.reference) ?? settings?.reference,
        countryCode:
          findObjectInArray(countries, 'value', settings.country_code) ?? settings?.country_code,
      },
    ],
    [],
  );

  const formatVatSettings = vatSettings =>
    vatSettings.reduce(
      (acc, settings) => [
        ...acc,
        {
          type: settings?.type,
          reference: parseInt(settings?.reference?.value, 10),
          country_code: settings?.countryCode?.value,
        },
      ],
      [],
    );

  const form = useForm({
    validationSchema,
    defaultValues: {
      name: service?.name ?? '',
      type: service?.type ?? 'handling',
      amount: service?.amount ?? null,
      isPercentage: service?.is_percentage ?? false,
      activeAt: service?.active_at ?? null,
      expiresAt: service?.expires_at ?? null,
      constraints: service?.constraints ?? [],
      filters: service?.filters ?? [],
      priority: service?.priority ?? 0,
      vatSettings: formattedVatSettings ?? [
        {
          ...VAT_SETTING_STRUCTURE,
          countryCode: findObjectInArray(countries, 'value', VAT_SETTING_STRUCTURE?.countryCode),
        },
      ],
    },
    onSubmit: data => {
      if (onSubmit) {
        onSubmit({
          ...data,
          constraints: formatConstraints(data.constraints),
          filters: formatFilters(data.filters),
          amount: parseInt(data.amount, 10),
          priority: parseInt(data.priority, 10),
          vatSettings: formatVatSettings(data.vatSettings),
        });
      }
    },
    onCancel,
  });

  useEffect(() => {
    let isMounted = true;

    const fetchAllVatRates = async () => {
      try {
        const response = await vatApi.getVatList();
        const { data: vatRateList = [] } = response;

        if (isMounted) {
          setVatRates(
            vatRateList.map(vatRate => ({
              ...vatRate,
              label: `${vatRate.rate}%`,
              value: vatRate.reference,
            })),
          );
        }
      } catch (err) {
        if (isMounted) {
          setVatRates([]);
        }
      }
    };

    fetchAllVatRates();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Form form={form}>
      <DefaultFormSection service={service} />
      <VatSettingsFormSection countries={countries} />
      <ConstraintsFormSection />
      <FiltersFormSection />
    </Form>
  );
};

ServiceCostForm.propTypes = {
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  service: PropTypes.object,
};

ServiceCostForm.defaultProps = {
  onSubmit: undefined,
  onCancel: undefined,
  service: undefined,
};

export default ServiceCostForm;
